// @ts-nocheck — small storefront script; `predictive-search-component` methods are not typed on Window.
/**
 * Iris desktop predictive: native <dialog> + showModal().
 * Exposes window.irisHeaderOpenDesktopSearch for inline onclick fallback (capture listeners can be blocked).
 */
(function () {
  'use strict';

  const OPEN_SELECTOR = '[data-iris-desktop-search-open]';
  const CLOSE_SELECTOR = '[data-iris-desktop-search-close]';
  const GO_SELECTOR = '[data-iris-desktop-search-go]';
  const TOOLBAR_SELECTOR = '.iris-desktop-search-dialog__toolbar';
  const DIALOG_ROOT_SELECTOR = 'dialog[data-iris-desktop-search-root]';
  /** Match Iris header cells that show desktop-only search (e.g. `iris-header__cell--hide-below-1024`). */
  const MQ_DESKTOP = '(min-width: 1024px)';

  /**
   * @returns {HTMLDialogElement | null}
   */
  function getDialog() {
    const all = document.querySelectorAll(DIALOG_ROOT_SELECTOR);
    const n = all.length;
    if (!n) return null;
    const el = all[n - 1];
    if (!(el instanceof HTMLDialogElement)) return null;
    return el;
  }

  /** `#header-group` is fixed z-index 60 — keep dialog under `body`. */
  function mountDialogUnderBody() {
    const all = Array.from(document.querySelectorAll(DIALOG_ROOT_SELECTOR));
    const dialog = all.length ? all[all.length - 1] : null;
    if (!dialog || !(dialog instanceof HTMLDialogElement)) return;
    for (const d of all) {
      if (d !== dialog) d.remove();
    }
    if (dialog.parentElement === document.body) return;
    document.body.appendChild(dialog);
  }

  function closeMenuDrawers() {
    for (const details of document.querySelectorAll('details.menu-drawer-container[open]')) {
      if (details instanceof HTMLDetailsElement) details.open = false;
    }
  }

  function setTriggersExpanded(expanded) {
    for (const el of document.querySelectorAll(OPEN_SELECTOR)) {
      if (el instanceof HTMLElement) el.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  /** @type {((e: PointerEvent) => void) | null} */
  let outsideDismissHandler = null;

  function detachOutsideDismiss() {
    if (!outsideDismissHandler) return;
    document.removeEventListener('pointerdown', outsideDismissHandler, true);
    outsideDismissHandler = null;
  }

  /** Close when clicking outside the search toolbar (input, results, footer, icons). */
  function attachOutsideDismiss() {
    detachOutsideDismiss();
    outsideDismissHandler = (e) => {
      const d = getDialog();
      if (!d?.open) return;
      const target = e.target;
      if (!(target instanceof Node)) return;
      const toolbar = d.querySelector(TOOLBAR_SELECTOR);
      if (toolbar?.contains(target)) return;
      d.close();
    };
    document.addEventListener('pointerdown', outsideDismissHandler, true);
  }

  /**
   * @param {HTMLElement} trigger
   */
  function openDesktopSearch(trigger) {
    if (!window.matchMedia(MQ_DESKTOP).matches) return;

    const dialog = getDialog();
    if (!dialog) return;
    if (dialog.open) return;

    closeMenuDrawers();
    mountDialogUnderBody();

    dialog.addEventListener(
      'close',
      () => {
        if (trigger.isConnected) trigger.focus();
      },
      { once: true }
    );

    try {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
      }
    } catch (err) {
      try {
        dialog.setAttribute('open', '');
      } catch {
        /* ignore */
      }
    }

    setTriggersExpanded(true);
    attachOutsideDismiss();
    requestAnimationFrame(() => {
      const input = dialog.querySelector('input[ref="searchInput"]');
      if (input instanceof HTMLInputElement) input.focus();
    });
  }

  /**
   * @param {Event} [event]
   * @param {HTMLElement} trigger
   */
  function irisHeaderOpenDesktopSearch(event, trigger) {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    if (event && typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    if (!(trigger instanceof HTMLElement)) return false;
    openDesktopSearch(trigger);
    return false;
  }

  window.irisHeaderOpenDesktopSearch = irisHeaderOpenDesktopSearch;

  /**
   * Mirror the "Enter key with no selected result" behavior from `predictive-search.js`
   * (`onSearchKeyDown` case 'Enter'): navigate to the full search results page with the
   * current input value as `q`. If the input is empty, do nothing.
   * @param {HTMLDialogElement} dialog
   */
  function submitFullSearchFromDialog(dialog) {
    const input = dialog.querySelector('input[ref="searchInput"]');
    if (!(input instanceof HTMLInputElement)) return;
    const term = input.value.trim();
    if (!term) return;

    const form = input.closest('form');
    const actionAttr = form instanceof HTMLFormElement ? form.getAttribute('action') : null;
    /** @type {string} */
    let baseUrl;
    if (actionAttr) {
      baseUrl = actionAttr;
    } else if (typeof Theme !== 'undefined' && Theme?.routes?.search_url) {
      baseUrl = Theme.routes.search_url;
    } else {
      baseUrl = '/search';
    }
    const searchUrl = new URL(baseUrl, location.origin);
    searchUrl.searchParams.set('q', input.value);
    window.location.href = searchUrl.toString();
  }

  function init() {
    if (window.__irisDesktopSearchInit) return;
    window.__irisDesktopSearchInit = true;

    const dialog = getDialog();
    if (!dialog) return;

    mountDialogUnderBody();

    dialog.addEventListener('close', () => {
      detachOutsideDismiss();
      setTriggersExpanded(false);
      const comp = dialog.querySelector('predictive-search-component');
      if (comp && typeof comp.resetSearch === 'function') {
        comp.resetSearch(false);
      }
    });

    document.addEventListener(
      'click',
      (e) => {
        const t = e.target;
        if (!(t instanceof Element)) return;
        const trigger = t.closest(OPEN_SELECTOR);
        if (!(trigger instanceof HTMLElement)) return;
        if (!window.matchMedia(MQ_DESKTOP).matches) return;
        e.preventDefault();
        openDesktopSearch(trigger);
      },
      true
    );

    dialog.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (t.closest(CLOSE_SELECTOR)) {
        e.preventDefault();
        dialog.close();
        return;
      }
      const goBtn = t.closest(GO_SELECTOR);
      if (goBtn) {
        e.preventDefault();
        submitFullSearchFromDialog(dialog);
      }
    });

    document.addEventListener(
      'toggle',
      (e) => {
        const details = e.target;
        if (!(details instanceof HTMLDetailsElement)) return;
        if (!details.classList.contains('menu-drawer-container')) return;
        if (!details.open) return;
        if (dialog.open) dialog.close();
      },
      true
    );

    document.addEventListener('shopify:section:load', mountDialogUnderBody);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
